import * as React from 'react';
import cn from 'classnames';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { useSubscriptionQuery } from 'src/api/queries/subscriptionQueries';
import CheckoutModal from 'src/admin-pages/Subscription/CheckoutModal';
import { isSelfServePlan } from 'src/admin-pages/Subscription/planVisibility';
import { useStripeCheckout } from 'src/hooks/useStripeCheckout';
import { TSubscriptionAlert } from 'src/types/TSubscription';
import { config } from 'src/utils/config';

import styles from './BillingBanner.module.scss';

const messages = defineMessages({
  // A trial no longer simply expires: at the end of it the account converts
  // to Pay as you go (plan-structure v2 Phase 4). With a card on file that is
  // seamless and usage is billed at list rates; WITHOUT one the account goes
  // view-only (`payment_required`) until a card is added. Say that, rather
  // than the old vague "upgrade to keep your assets".
  // The floor is deliberately NOT quoted here. This banner has one job — get a
  // card on file — and the $5 minimum does not apply during the trial, so
  // naming a price mid-trial invites "am I being charged now?". It IS quoted
  // where a price belongs: the plan card ("From $5/mo, fully applied to your
  // usage"), the rate card, and the trial-END copy below, which is the moment
  // the floor actually starts to apply.
  trialCountdown: {
    id: 'billingBanner.trialCountdown',
    defaultMessage:
      'Your free trial ends {trialEndsAt, date, medium}. Add a payment method to '
      + 'automatically switch to Pay as you go and keep uploads and processing running '
      + 'after trial.',
  },
  trialEndingToday: {
    id: 'billingBanner.trialEndingToday',
    defaultMessage:
      'Your free trial ends today. Add a payment method to automatically switch to Pay as '
      + 'you go and keep uploads and processing running after trial.',
  },
  pastDue: {
    id: 'billingBanner.pastDue',
    defaultMessage:
      'A recent payment failed. Update your billing to avoid interruption.',
  },
  // Trial-end copy, fixed by the pricing decision of 2026-08-12. Two things it
  // has to do: quote the floor as VALUE ("every dollar usable", never a fee),
  // and make the no-card outcome unthreatening — assets stay safe, read-only.
  paymentRequired: {
    id: 'billingBanner.paymentRequired',
    defaultMessage:
      // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
      'Add a card to continue on Pay-as-you-go — from ${minimum}/month, every dollar '
      + 'usable. No card? Your assets stay safe in a free read-only account.',
  },
  // Without a published floor to quote (a catalog with no minimum), fall back to
  // the pre-decision wording rather than printing "from $0/month".
  paymentRequiredNoMinimum: {
    id: 'billingBanner.paymentRequiredNoMinimum',
    defaultMessage:
      'Your trial ended — add a payment method to resume uploads and processing.',
  },
  // DORMANT: an idle cycle billed $0 and paused the account. It is NOT
  // delinquent — the card is on file and nothing is owed — so this must not ask
  // for a payment method. Any billable action resumes it server-side, so the
  // instruction is simply "use it".
  dormant: {
    id: 'billingBanner.dormant',
    defaultMessage:
      "Your account is paused — you weren't charged for a month with no activity. "
      + 'Upload or process anything to pick up where you left off.',
  },
  // Same state, but this account stores more than the free dormant allowance,
  // so a paused month is not free for it. Never tell a customer they weren't
  // charged when the invoice will say otherwise.
  dormantBilledStorage: {
    id: 'billingBanner.dormantBilledStorage',
    defaultMessage:
      'Your account is paused — no processing was billed, but stored data above '
      + '{allowance} GB still is. Upload or process anything to resume.',
  },
  addPaymentLink: {
    id: 'billingBanner.addPaymentLink',
    defaultMessage: 'Add payment method',
  },
  // Usage threshold alerts (Phase E). `pct` is the crossed threshold; the
  // dimension label is pre-formatted from `dimensionLabels` below.
  usageApproaching: {
    id: 'billingBanner.usageApproaching',
    defaultMessage: "You've used {pct}% of your {dimension} allowance this cycle.",
  },
  usageOver: {
    id: 'billingBanner.usageOver',
    defaultMessage:
      "You've used {pct}% of your {dimension} allowance this cycle. Usage beyond your plan may incur overage charges.",
  },
  manageLink: {
    id: 'billingBanner.manageLink',
    defaultMessage: 'Go to billing',
  },
  // SPEND LIMIT reached — work is stopped RIGHT NOW by the customer's own
  // setting. Ranks above every usage alert (those warn about a bill; this one
  // explains why an upload just failed) and below `payment_required`.
  spendCapBlocked: {
    id: 'billingBanner.spendCapBlocked',
    defaultMessage:
      // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
      'Uploads and processing are paused — this cycle reached the ${cap} spend limit you set. '
      + 'Your assets are safe and downloads keep working.',
  },
  trialLimitBlocked: {
    id: 'billingBanner.trialLimitBlocked',
    defaultMessage:
      'Uploads and changes are paused — your free trial reached its {limits} limit. '
      + 'You can still view, share and delete.',
  },
  choosePlanLink: {
    id: 'billingBanner.choosePlanLink',
    defaultMessage: 'Choose a plan',
  },
  spendCapLink: {
    id: 'billingBanner.spendCapLink',
    defaultMessage: 'Change spend limit',
  },
  // The dollar-based alert (dimension `spend_usd`) is not a share of an
  // allowance, so it cannot borrow the "% of your {dimension}" wording — it
  // would read "75% of your spend_usd allowance". Its own line instead.
  spendApproaching: {
    id: 'billingBanner.spendApproaching',
    defaultMessage:
      "You've used {pct}% of your spend limit for this cycle. At 100% uploads and "
      + 'processing pause until you raise it.',
  },
});

// Human labels for the billable dimensions the backend alerts on.
const dimensionLabels = defineMessages({
  storage_gb: {
    id: 'billingBanner.dimension.storage',
    defaultMessage: 'storage',
  },
  transcode_minutes: {
    id: 'billingBanner.dimension.transcode',
    defaultMessage: 'video transcoding',
  },
  ai_credits: {
    id: 'billingBanner.dimension.aiCredits',
    defaultMessage: 'credits',
  },
  cdn_bytes: {
    id: 'billingBanner.dimension.cdn',
    defaultMessage: 'CDN delivery',
  },
  asset_count: {
    id: 'billingBanner.dimension.assets',
    defaultMessage: 'asset',
  },
});

const SUBSCRIPTION_ROUTE = '/admin/subscription';

// Deep link that opens the spend-limit control on the Subscription page,
// rather than dropping the reader at the top of it to hunt for the setting
// that is currently blocking them.
const SPEND_LIMIT_ROUTE = `${SUBSCRIPTION_ROUTE}?purchase=spendLimit`;

// The dollar-based alert dimension. Not a meter — it has no allowance to be a
// percentage of, so it gets its own copy rather than the per-dimension line.
const SPEND_DIMENSION = 'spend_usd';

/**
 * "Add payment method" opens the card-capturing checkout RIGHT HERE, on
 * whatever page the reader is on — the banner's own embedded-checkout modal.
 *
 * It used to be a link to the Subscription page, which did nothing at all when
 * the reader was already there, and elsewhere only navigated: a CTA that
 * promised an action and merely moved you. The card is captured by a $0 PAYG
 * Checkout Session (`payment_method_collection: 'always'`), the same mechanism
 * the trial→PAYG conversion relies on.
 *
 * Enterprise (non-self-serve) accounts are invoiced, so they never see this —
 * the banners it appears on (`trialing`, `payment_required`) are self-serve
 * states by construction.
 */
const AddPaymentMethodCta: React.FC = () => {
  const { enabled, isLoading, clientSecret, startCheckout, handleComplete, closeCheckout } =
    useStripeCheckout();

  // Without Stripe configured there is nothing to open; fall back to the page
  // so the CTA is never a no-op.
  if (!enabled) {
    return (
      <Link className={styles.link} to={SUBSCRIPTION_ROUTE}>
        <FormattedMessage {...messages.addPaymentLink} />
      </Link>
    );
  }

  return (
    <>
      <button
        type="button"
        className={styles.linkButton}
        onClick={() => startCheckout(PAYG_PLAN_CODE)}
        disabled={isLoading}
      >
        <FormattedMessage {...messages.addPaymentLink} />
      </button>
      <CheckoutModal
        clientSecret={clientSecret}
        onComplete={handleComplete}
        onClose={closeCheckout}
      />
    </>
  );
};

// The $0, usage-billed plan whose checkout captures a card.
const PAYG_PLAN_CODE = 'PAYG';

// "5" for 5, "4.50" for 4.5 — a floor quoted in copy should read as money
// without trailing ".00" noise.
function formatUsd(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

// Days from now until `iso` (UTC), floored at 0. Null when unparseable.
function daysUntil(iso?: string | null): number | null {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  const ms = end - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

// The single most-severe alert to surface (highest crossed threshold). One
// banner line keeps the app chrome quiet even when several dims are over.
function pickTopAlert(
  alerts?: TSubscriptionAlert[] | null,
): TSubscriptionAlert | null {
  if (!alerts || alerts.length === 0) return null;
  return alerts.reduce(
    (top, a) => (a.threshold_pct > top.threshold_pct ? a : top),
    alerts[0],
  );
}

/**
 * Persistent, app-wide billing banner. Rendered in AppShell so it shows on
 * every authenticated page. Priority (most urgent first):
 *   - `past_due`   -> a dunning warning
 *   - `trialing`   -> a trial countdown with an upgrade nudge
 *   - usage alert  -> approaching (75%) / over (100/125%) a billable quota
 * Silent otherwise. Only owners/admins get subscription data (the query 401s
 * otherwise), so this naturally no-ops for seat users. Silent for enterprise
 * (non-self-serve) accounts too — see the gate below.
 */
export const BillingBanner: React.FC = () => {
  const intl = useIntl();
  const { data, isError } = useSubscriptionQuery();

  if (!config.showSubscription || isError || !data) return null;

  // Enterprise (`CUSTOM-…`, self_serve = 0) accounts are invoiced against a
  // contract: overages are handled by accounts, nothing is billed in-app, and
  // every state this banner reports is a self-serve state. Silent for them —
  // allowance figures stay visible on the Subscription page.
  if (!isSelfServePlan(data)) return null;

  const status = data.status;

  // Card-less PAYG after trial auto-convert: most urgent — the account is
  // view-only until a payment method is added (Subscription page CTA).
  if (status === 'payment_required') {
    const minimum = data.details?.monthly_minimum_usd ?? 0;
    return (
      <div className={cn(styles.banner, styles.pastDue)} role="alert">
        <span>
          {minimum > 0 ? (
            <FormattedMessage
              {...messages.paymentRequired}
              values={{ minimum: formatUsd(minimum) }}
            />
          ) : (
            <FormattedMessage {...messages.paymentRequiredNoMinimum} />
          )}
        </span>
        <AddPaymentMethodCta />
      </div>
    );
  }

  // Spend limit reached: writes are being refused right now. Ranks directly
  // below `payment_required` because it is the same kind of message — work has
  // stopped and there is one specific thing to do about it — and above every
  // usage alert, which only warns about a bill. `blocked` is the server's own
  // gate decision, so this banner and the 503s cannot disagree.
  if (data.spend_cap_status?.blocked) {
    return (
      <div className={cn(styles.banner, styles.usageOver)} role="alert">
        <span>
          <FormattedMessage
            {...messages.spendCapBlocked}
            values={{ cap: formatUsd(data.spend_cap_status.cap_usd) }}
          />
        </span>
        <Link className={styles.link} to={SPEND_LIMIT_ROUTE}>
          <FormattedMessage {...messages.spendCapLink} />
        </Link>
      </div>
    );
  }

  // Free-trial cap reached: same kind of message as the spend limit — work has
  // stopped, and choosing a plan is the one thing that restarts it. The list is
  // the server's own gate verdict (`trial_guard`), so banner and 503s agree.
  const trialLimits = data.trial_limits_reached ?? [];
  if (trialLimits.length > 0) {
    const limits = intl.formatList(
      trialLimits.map((dim) => {
        const label = dimensionLabels[dim as keyof typeof dimensionLabels];
        return label ? intl.formatMessage(label) : dim;
      }),
      { type: 'conjunction' },
    );
    return (
      <div className={cn(styles.banner, styles.usageOver)} role="alert">
        <span>
          <FormattedMessage {...messages.trialLimitBlocked} values={{ limits }} />
        </span>
        <Link className={styles.link} to={`${SUBSCRIPTION_ROUTE}/plans`}>
          <FormattedMessage {...messages.choosePlanLink} />
        </Link>
      </div>
    );
  }

  // Dormant: paused after an idle cycle, nothing owed. Ranks below
  // `payment_required` (which needs an action) and above the trial countdown,
  // and deliberately carries NO payment CTA — the card is already on file and
  // the next upload wakes the account by itself. `dormant` is what separates
  // this from an over-quota `read_only` account, which must keep its own copy.
  if (data.dormant) {
    // Free only up to the plan's dormant storage allowance (decimal GB, as the
    // backend meters it). Past it, stored data is still billed.
    const allowanceGb = data.details?.dormant_storage_gb ?? null;
    const heldGb = (data.usage?.storage?.used ?? 0) / 1e9;
    const billedForStorage = allowanceGb != null && heldGb > allowanceGb;
    return (
      <div className={cn(styles.banner, styles.usageWarn)} role="status">
        <span>
          {billedForStorage ? (
            <FormattedMessage
              {...messages.dormantBilledStorage}
              values={{ allowance: allowanceGb }}
            />
          ) : (
            <FormattedMessage {...messages.dormant} />
          )}
        </span>
        <Link className={styles.link} to={SUBSCRIPTION_ROUTE}>
          <FormattedMessage {...messages.manageLink} />
        </Link>
      </div>
    );
  }

  if (status === 'past_due') {
    return (
      <div className={cn(styles.banner, styles.pastDue)} role="alert">
        <span>
          <FormattedMessage {...messages.pastDue} />
        </span>
        <Link className={styles.link} to={SUBSCRIPTION_ROUTE}>
          <FormattedMessage {...messages.manageLink} />
        </Link>
      </div>
    );
  }

  if (status === 'trialing') {
    const days = daysUntil(data.trial_ends_at);
    if (days === null) return null; // indefinite trial — no banner
    return (
      <div className={cn(styles.banner, styles.trial)} role="status">
        <span>
          {/* On the last day the DATE is the least useful phrasing available —
              "ends Sep 11" on Sep 11 makes the reader do the comparison — so
              day zero keeps its own wording. */}
          {days <= 0 ? (
            <FormattedMessage {...messages.trialEndingToday} />
          ) : (
            <FormattedMessage
              {...messages.trialCountdown}
              values={{ trialEndsAt: new Date(data.trial_ends_at as string) }}
            />
          )}
        </span>
        <AddPaymentMethodCta />
      </div>
    );
  }

  // A `spend_usd` alert is a percentage of the limit as it stood when the
  // hourly sweep ran. If no limit applies now — cleared by a plan change, or
  // removed by the owner — the row is measured against a number that no longer
  // exists, and the server retracts it. This drops it client-side too: dev
  // showed an account being told "You've used 100% of your spend limit" with no
  // limit set at all, and a banner that outlives its own subject is worse than
  // no banner. Quota alerts (storage, transcode, credits) are unaffected — they
  // measure allowances, which exist regardless.
  const alerts = (data.alerts ?? []).filter(
    (a) => a.dimension !== SPEND_DIMENSION || Boolean(data.spend_cap_status),
  );
  const alert = pickTopAlert(alerts);
  if (alert) {
    const isOver = alert.threshold_pct >= 100;
    // A spend alert measures dollars against a limit, not consumption against
    // an allowance — the generic line would render "75% of your spend_usd
    // allowance". (A 100% spend alert normally arrives with `blocked` set and
    // is caught by the branch above; this covers the account that has already
    // raised its limit, and the metered-from-zero alert measured against the
    // cheapest paid fee, which never blocks anything.)
    const isSpend = alert.dimension === SPEND_DIMENSION;
    const dimMsg = (dimensionLabels as Record<string, typeof messages.pastDue>)[
      alert.dimension
    ];
    const dimension = dimMsg
      ? intl.formatMessage(dimMsg)
      : alert.dimension;
    return (
      <div
        className={cn(styles.banner, isOver ? styles.usageOver : styles.usageWarn)}
        role={isOver ? 'alert' : 'status'}
      >
        <span>
          {isSpend ? (
            <FormattedMessage
              {...messages.spendApproaching}
              values={{ pct: alert.threshold_pct }}
            />
          ) : (
            <FormattedMessage
              {...(isOver ? messages.usageOver : messages.usageApproaching)}
              values={{ pct: alert.threshold_pct, dimension }}
            />
          )}
        </span>
        <Link
          className={styles.link}
          to={isSpend ? SPEND_LIMIT_ROUTE : SUBSCRIPTION_ROUTE}
        >
          <FormattedMessage
            {...(isSpend ? messages.spendCapLink : messages.manageLink)}
          />
        </Link>
      </div>
    );
  }

  return null;
};

export default BillingBanner;
